import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import brandLocales from '@brand-locales';
import ru from './locales/ru';
import en from './locales/en';
import { DEFAULT_LANG, detectLanguage, LANGUAGES, type Lang } from './detect';
import { persistLanguage } from './persist';
import { withBrandOverrides } from './merge';
import type { LocaleBundle } from './types';

// i18n кабинета. Два языка — RU и EN, дефолт RU.
//
// Оба словаря попадают в бандл статически, а не догружаются по выбору языка.
// Так сделано осознанно: словарь на ~1000 ключей — это ~20 КБ gzip на язык
// (против 364 КБ одного recharts), зато мы получаем работающий fallbackLng
// (пропущенный EN-ключ показывает русский текст, а не сырой `stat.columns.epc`),
// мгновенное переключение без сетевого запроса и синхронный init — то есть
// main.tsx не превращается в асинхронный bootstrap, а тесты инициализируют i18n
// тем же кодом, что и приложение.
//
// Бренд остаётся build-time (алиас @brand-locales, как @brand-current): в бандл
// уезжают оверрайды только своего бренда.

export const NAMESPACES = [
  'auth',
  'common',
  'dashboard',
  'dicts',
  'faq',
  'landings',
  'nav',
  'news',
  'payments',
  'referals',
  'rules',
  'settings',
  'sources',
  'stat',
  'streams',
  'support',
  'tds',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export { DEFAULT_LANG, LANGUAGES, detectLanguage, type Lang } from './detect';

const resources: Record<Lang, LocaleBundle> = {
  ru: withBrandOverrides(ru, brandLocales.ru),
  en: withBrandOverrides(en, brandLocales.en),
};

// lang передаётся в тестах, чтобы не зависеть от localStorage/navigator в jsdom;
// приложение вызывает без аргумента и получает выбор партнёра.
export function initI18n(lang?: Lang): Lang {
  const lng = lang ?? detectLanguage();

  if (!i18next.isInitialized) {
    i18next.use(initReactI18next).init({
      lng,
      fallbackLng: DEFAULT_LANG,
      supportedLngs: LANGUAGES,
      resources,
      ns: NAMESPACES,
      defaultNS: 'common',
      // Экранирование делает React — иначе строки с кавычками и амперсандами
      // приезжают в разметку как &#39; / &amp;.
      interpolation: { escapeValue: false },
      // Ресурсы уже в памяти, ждать нечего: init должен отработать синхронно,
      // иначе первый рендер увидит пустой t(). (В i18next ≥25 опция называется
      // initAsync — прежний initImmediate удалён.)
      initAsync: false,
      returnNull: false,
      // Ключи вида 'stat.columns.epc' — точка как разделитель вложенности,
      // двоеточие как разделитель namespace (t('stat:columns.epc')).
      keySeparator: '.',
      nsSeparator: ':',
    });
  } else if (i18next.language !== lng) {
    void i18next.changeLanguage(lng);
  }

  persistLanguage(lng);

  return lng;
}

// Переключение языка из UI: сначала запоминаем выбор (localStorage + cookie +
// <html lang>), потом переключаем i18next — подписанные компоненты
// перерисовываются сами через useTranslation.
export async function setLanguage(lang: Lang) {
  persistLanguage(lang);
  // Тот же язык — дальше не идём: changeLanguage эмитит languageChanged и на
  // прежнем языке, а на этом событии висит invalidateQueries (app/queryLanguage),
  // то есть клик по уже выбранному языку перезапрашивал весь активный кабинет.
  // Выбор при этом всё равно запоминаем: язык мог быть определён по браузеру, а
  // клик делает его осознанным.
  if (lang === currentLanguage()) return;

  await i18next.changeLanguage(lang);
}

export function currentLanguage(): Lang {
  const lng = i18next.resolvedLanguage ?? i18next.language;
  return (LANGUAGES as readonly string[]).includes(lng) ? (lng as Lang) : DEFAULT_LANG;
}

export { i18next as i18n };
